import { SiteUrls } from '@tableau/shared-urls';
import { ButtonType, ButtonWidget, FlyoutListMenuWidget, HorizontalOriginOption, InteractionState, MenuItemInfo, VerticalOriginOption } from '@tableau/widgets';
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { connect } from 'react-redux';

import { AuthenticationTypeResolutionService } from '~src/authentication/services/AuthenticationTypeResolutionService';
import { SiteRolesUtil } from '~src/userManagement/services/resources/SiteRolesUtil';

import { Messages } from '../../codegen/Localize';
import { DeprecatedReactMenu } from '../../common/react/components/Menu';
import { Location } from '../../common/react/Location';
import { UserActions } from '../../common/react/UserActions';
import { PageHeaderButtonClickEvent, TelemetryEvents } from '../../common/telemetryEvents/TelemetryEvents';
import { Telemetry } from '../../commonInfra/services/Telemetry';
import { AuthenticationType } from '../../settings/types/IServerSettingsUnauthenticated';
import { FeatureFlags } from '../../ts/FeatureFlags';
import { IUserInfo } from '../../ts/models/IUserInfo';
import { ProfilePictureStyles } from '../content/styles/ProfilePictureStyles';
import { UserProfilePicture } from '../content/UserProfilePicture';
import { NavLocationSelectors } from '../selectors/NavigationStateSelectors';
import {
  selectSessionUser, SessionServerSelectors,
  SessionSiteSelectors, SessionUserSelectors,
} from '../selectors/SessionInfoSelectors';
import { siteUrlsSelector } from '../selectors/SiteUrlsSelectors';
import { selectSpaceInfo } from '../selectors/SpaceInfoSelectors';
import { ServerUrls } from '../ServerUrls';
import { ICollectionInfo } from '../spaces/Collections/ICollectionInfo';
import { AppState } from '../state/AppState';
import { isSingleCollectionSpace,SpaceInfo } from '../state/SpaceInfo';
import { MapStateToProps } from '../utils/ConnectUtils';
import { HeaderStyles } from './HeaderStyles';

type UserProfilePictureButtonProps = UserProfilePicture.Props & DeprecatedReactMenu.ButtonProps;

export class UserProfilePictureButton extends React.Component<UserProfilePictureButtonProps> {
  public render(): JSX.Element {
    return <UserProfilePicture {...this.props} />;
  }
}

export interface UserMenuOwnProps {
  readonly style?: any;

  // The "in*area" props should be state props, but the old top bar computed them differently
  // than the current implementation does. We could get these props directly from the UserMenu
  // connected component instead of passing them in from ConnectedPageHeaderMenus.
  readonly inServerArea: boolean;
  readonly inSiteContentArea: boolean;
}

export interface UserMenuPropsFromReduxState {
  readonly siteUrls: SiteUrls;
  readonly user: IUserInfo;
  readonly spaceInfo: SpaceInfo;
  readonly isLogoutEnabled: boolean;
  readonly isSessionServerAdmin: boolean;
  readonly isSiteAdmin: boolean;
  readonly isOnlineEnvironment: boolean;
  readonly isSingleSiteServerAdmin: boolean;
  readonly isInVizViewerArea: boolean;
  readonly shouldShowPersonalSpace: boolean;
}

export interface UserMenuPropsFromServices {
  readonly userActions: UserActions;
  readonly authenticationType: AuthenticationType;
}

export interface UserMenuState {
  readonly isMenuOpen: boolean;
}

export type UserMenuViewComponentProps = UserMenuOwnProps & UserMenuPropsFromReduxState & UserMenuPropsFromServices;

const buttonStyles: React.CSSProperties = {
  cursor: 'pointer'
};

const profilePictureMarginSize = (HeaderStyles.TopBarHeight - ProfilePictureStyles.Sizes.ExtraSmallIconSize) / 2;
const divAroundButtonStyle: React.CSSProperties = { paddingRight: '10px', marginTop: profilePictureMarginSize, marginBottom: profilePictureMarginSize, marginRight: '-2px'};


export class UserMenuViewComponent extends React.Component<UserMenuViewComponentProps, UserMenuState> {
  private buttonEl: HTMLElement | undefined;
  private serverUrls: ServerUrls;
  static signOut = 'signOut';
  static myAccountServer = 'myAccountSettingsServer';
  static myAccountSettings = 'myAccountSettings';
  static myContent = 'myContent';
  static myContentSiteOnly = 'myContentSiteOnly';
  static userStartPageChange = 'userStartPageChange';
  static defaultStartPageChange = 'defaultStartPageChange';
  static startPageChangeSubMenu = 'StartPageChangeSubMenu';
  static siteStartPageChange = 'siteStartPageChange';
  static personalSpace = 'personalSpace';

  constructor(props: UserMenuViewComponentProps) {
    super(props);
    this.state = {
      isMenuOpen: false,
    };
    this.serverUrls = ServerUrls.getInstance();
  }

  private close = (): void => {
    this.setState({ isMenuOpen: false });
  }

  private toggleMenuOpen = () => {
    Telemetry.sendEvent<PageHeaderButtonClickEvent>({
      type: TelemetryEvents.PageHeaderButtonClick,
      button: 'user-menu',
      action: this.state.isMenuOpen ? 'close' : 'open'
    });
    this.setState({ isMenuOpen: !this.state.isMenuOpen });
  }

  private isOnlineSiteAdmin(): boolean {
    return this.props.isSiteAdmin && this.props.isOnlineEnvironment;
  }

  private isMultiSiteServerAdmin(): boolean {
    return this.props.isSessionServerAdmin && !this.props.isSingleSiteServerAdmin;
  }

  private get isUserAdmin(): boolean {
    return this.props.isSiteAdmin || this.props.isSessionServerAdmin;
  }

  private handleButtonRef = (ref: ButtonWidget) => {
    this.buttonEl = ref ? ReactDOM.findDOMNode(ref) as HTMLElement : undefined;
  }

  private handleSelect = (value: string) => {
    switch (value) {
      case UserMenuViewComponent.signOut: {
        this.props.userActions.signout();
        break;
      }
      case UserMenuViewComponent.myAccountServer: {
        Location.instance.setUrl(this.serverUrls.userSettingsUrl(this.props.user.domainName, this.props.user.username));
        break;
      }
      case UserMenuViewComponent.myAccountSettings: {
        Location.instance.setUrl(this.props.siteUrls.userSettingsUrl(this.props.user.username, this.props.user.domainName));
        break;
      }
      case UserMenuViewComponent.personalSpace: {
        Location.instance.setUrl(this.props.siteUrls.personalSpaceUrl());
        break;
      }
      case UserMenuViewComponent.myContent: {
        Location.instance.setUrl(this.props.siteUrls.userUrl(this.props.user.username, this.props.user.domainName));
        break;
      }
      case UserMenuViewComponent.myContentSiteOnly:
      case UserMenuViewComponent.userStartPageChange: {
        this.props.userActions.changeUserStartPage();
        break;
      }
      case UserMenuViewComponent.defaultStartPageChange: {
        this.props.userActions.changeServerDefaultStartPage();
        break;
      }
      case UserMenuViewComponent.siteStartPageChange: {
        this.props.userActions.changeSiteStartPage();
      }
    }

    this.close();
  }

  private getButtonContent = (state: InteractionState): JSX.Element => {
    let divStyle: React.CSSProperties = {border: '1px solid transparent'};
    return (<UserProfilePicture
              profileDisplayName={this.props.user ? this.props.user.displayName : ''}
              imageUrl={this.props.user ? this.props.user.userImageUrl : ''}
              imageSize={ProfilePictureStyles.Sizes.ExtraSmallIconSize}
              fontSize={ProfilePictureStyles.FontSizes.ExtraSmallIconFontSize}
              divStyle={divStyle}
              useInvertedColors={this.props.isInVizViewerArea}/>);
  }

  //TODO: Update https://opengrok/source/xref/teams_near/product-tests/java/libraries/VizportalUILib/src/com/tableausoftware/test/server/vizportalUILib/pageObjects/components/UserMenu.java#37
  render() {
    let menuItemInfos: MenuItemInfo[] = [];

    if (this.props.inServerArea) {
      this.props.shouldShowPersonalSpace && menuItemInfos.push({
        value: UserMenuViewComponent.personalSpace,
        displayValue: Messages.personalSpaceSiteOnly(),
        disabled: true,
      });

      menuItemInfos.push({
        value: UserMenuViewComponent.myContentSiteOnly,
        displayValue: Messages.myContentSiteOnly(),
        disabled: true,
      });

      menuItemInfos.push({
        value: UserMenuViewComponent.myAccountServer,
        displayValue: Messages.myAccountSettingsServer()
      });
    } else {
      this.props.shouldShowPersonalSpace && menuItemInfos.push({
        value: UserMenuViewComponent.personalSpace,
        displayValue: Messages.navigation_personalSpace()
      });
      menuItemInfos.push({
        value: UserMenuViewComponent.myContent,
        displayValue: Messages.myContent()
      });
      menuItemInfos.push({
        value: UserMenuViewComponent.myAccountSettings,
        displayValue: Messages.myAccountSettings()
      });
    }

    // Change start page

    const isPrivateCollectionSpace = isSingleCollectionSpace(this.props.spaceInfo)
      && this.props.spaceInfo.item
      && this.props.spaceInfo.item.contentType === 'collection'
      && (this.props.spaceInfo.item as ICollectionInfo).visibility === 'private';
    const submenuIsEnabled = this.props.inSiteContentArea || this.isUserAdmin;
    const submenuSiteStartPageEnabled = this.props.inSiteContentArea && !isPrivateCollectionSpace;
    if (this.props.isSessionServerAdmin || this.isOnlineSiteAdmin()) {

      let subMenuItemInfos: MenuItemInfo[] = [];

      subMenuItemInfos.push({
        value: UserMenuViewComponent.userStartPageChange,
        displayValue: Messages.defaultStartPageForMe(),
      });
      if (FeatureFlags.siteDefaultStartPage &&
        (this.isOnlineSiteAdmin() ||
          this.isMultiSiteServerAdmin())) {
        subMenuItemInfos.push({
          value: UserMenuViewComponent.siteStartPageChange,
          displayValue: Messages.defaultStartPageForSite(),
          disabled: !submenuSiteStartPageEnabled
        });
      }
      if (this.props.isSessionServerAdmin) {
        subMenuItemInfos.push({
          value: UserMenuViewComponent.defaultStartPageChange,
          displayValue: Messages.defaultStartPageForEveryone(),
          disabled: !submenuSiteStartPageEnabled
        });
      }
      menuItemInfos.push({
        value: UserMenuViewComponent.startPageChangeSubMenu,
        displayValue: Messages.defaultStartPageSubMenu(),
        menuItemInfos: subMenuItemInfos,
        title: submenuIsEnabled ? '' : Messages.defaultStartPageRestriction(),
        disabled: !submenuIsEnabled,
        subMenuStyle: {fontWeight: 'normal'}
      });
    } else {
      menuItemInfos.push({
        value: UserMenuViewComponent.userStartPageChange,
        displayValue: Messages.userStartPageChange(),
      });
    }

    // Logout

    if (this.props.isLogoutEnabled) {
      const switchUser = this.props.authenticationType === AuthenticationType.Sspi ||
        this.props.authenticationType === AuthenticationType.Kerberos;
      menuItemInfos.push({
        value: UserMenuViewComponent.signOut,
        displayValue: switchUser ? Messages.switchUser() : Messages.signOut(),
      });
    }

    return (
      <>
        <div style={divAroundButtonStyle}>
          <ButtonWidget
            buttonType={ButtonType.IconOutline}
            handleClick={this.toggleMenuOpen}
            ref={this.handleButtonRef}
            testId="flyout-list-menu"
            style={buttonStyles}
            getContent={this.getButtonContent}
            aria-label={Messages.userMenuButton()}
            title={this.state.isMenuOpen ? Messages.userMenuButtonTooltipClose() : Messages.userMenuButtonTooltipOpen()}
          />
        </div>
        {this.state.isMenuOpen &&
          <FlyoutListMenuWidget
            menuStyle={{ fontWeight: 'normal' }}
            allowAutoPosition
            anchorEl={this.buttonEl}
            anchorOrigin={{
              vertical: VerticalOriginOption.bottom,
              horizontal: HorizontalOriginOption.left,
            }}
            menuItemInfos={menuItemInfos}
            onSelect={this.handleSelect}
            requestCloseAll={this.close}
            testId="flyout-list-menu"
            toggleEl={this.buttonEl}
          />
        }
      </>
    );
  }
}

export namespace ReduxConnectedUserMenu {

  const mapStateToProps: MapStateToProps<UserMenuPropsFromReduxState> = (state: AppState) => ({
    siteUrls: siteUrlsSelector(state),
    user: selectSessionUser(state),
    spaceInfo: selectSpaceInfo(state),
    isLogoutEnabled: SessionSiteSelectors.isLogoutEnabled(state),
    isSessionServerAdmin: SessionUserSelectors.isSessionServerAdmin(state),
    isSiteAdmin: SiteRolesUtil.isSiteAdmin(SessionSiteSelectors.siteRole(state)),
    isOnlineEnvironment: SessionServerSelectors.isOnlineEnvironment(state),
    isSingleSiteServerAdmin: SessionUserSelectors.isSingleSiteServerAdmin(state),
    isInVizViewerArea: NavLocationSelectors.inVizViewerArea(state),
    shouldShowPersonalSpace: SessionSiteSelectors.shouldShowPersonalSpace(state),
  });

  export const HOC = connect(mapStateToProps);
}

export const UserMenu: React.ComponentType<UserMenuOwnProps> = function UserMenu(props: UserMenuOwnProps) {
  const ReduxConnected = ReduxConnectedUserMenu.HOC(
    UserMenuViewComponent
  );
  const userActions = new UserActions();
  return (<ReduxConnected
    {...props}
    userActions={userActions}
    authenticationType={AuthenticationTypeResolutionService.authenticationType()}
  />);
};